/**
 * ObjectNode - Fixed-size dark node card.
 *
 * Layout (no expand/collapse):
 *   - Card width is **measured once** at mount from the natural single-line
 *     width of the display name + chrome, capped at MAX_NODE_WIDTH.
 *   - Title is clamped to MAX_TITLE_LINES lines with ellipsis.
 *   - Default state: leading icon = product logo + display name.
 *   - Hover state: a small chip floats **above** the card showing
 *     `<logo> <typeLabel>`, and the leading icon inside the card swaps
 *     from the product logo to the resource-type secondary icon. Card
 *     dimensions never change.
 *
 * @jsxRuntime classic
 * @jsx jsx
 */
/* eslint-disable @atlaskit/ui-styling-standard/enforce-style-prop -- Inline styles are dynamic per-node measurements (selection state, focus state, computed widths/colors); migrating to css/cssMap for these would require significant refactoring of measurement logic. Tracked for follow-up. */
/* eslint-disable @atlaskit/design-system/use-tokens-typography -- Hard-coded typography for the dark graph node card; will migrate to typography tokens in a follow-up. */
/* eslint-disable @atlaskit/design-system/ensure-design-token-usage -- Hard-coded colors for the dark graph node theme; will migrate to design tokens in a follow-up. */

import React, { memo, useLayoutEffect, useRef, useState } from 'react';

import {
	EdgeLabelRenderer,
	Handle,
	Position,
	type NodeProps,
	type Node,
	useInternalNode,
	useReactFlow,
} from '@xyflow/react';

import { getDocument } from '@atlaskit/browser-apis';
import { jsx } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';

import {
	useGraphSelection,
	computeNodeOpacity,
	isNodeTypeLabelVisible,
} from '../context/GraphSelectionContext';
import { useProductIcon } from '../services/useProductIcon';

import { LoadingPerimeterRing, useIsNodeLoadingData } from './LoadingPerimeterRing';

export type ObjectNodeData = {
	id: string;
	typename: string;
	/**
	 * Pre-resolved, user-facing type label (e.g. `"GitHub Repository"` for
	 * an `ExternalRepository` backed by a GitHub connector). Produced by
	 * the display-layer `toDisplayNode` helper upstream; consumers should
	 * use this directly instead of re-deriving a label from `typename`.
	 */
	typenameDisplay?: string;
	displayName: string | null;
	url: string | null;
	properties: Record<string, any>;
	/**
	 * Legacy 3P integration hint — still read by a handful of panel
	 * components. New display code paths prefer `typenameDisplay` +
	 * `productLogoUrl` + `productIcon`.
	 */
	integration?: string;
	/** Pre-resolved product-logo icon element (resource-type or product). */
	productIcon?: JSX.Element | null;
	/**
	 * Resource-type icon **importPath** (e.g. `@atlaskit/icon/core/page`)
	 * from the product registry. Resolved lazily via `useProductIcon`.
	 */
	productIconImportPath?: string;
	/** Pre-resolved product-logo data URI for inline `<img>` rendering. */
	productLogoUrl?: string;
	/**
	 * For user-typed nodes: profile-picture URL surfaced from hydration
	 * (`properties.picture` on AtlassianAccountUser).
	 */
	pictureUrl?: string;
	/**
	 * When true on first render, the node mounts at `opacity: 0` and
	 * transitions to `opacity: 1` over ~300 ms. Read only at mount; the
	 * flag can be toggled on/off in subsequent renders without retriggering
	 * the fade (so the upstream `AppearingNodesAtom` TTL doesn't interact
	 * with the component's internal `hasMounted` state).
	 */
	isAppearing?: boolean;
	/**
	 * When true, render a rotating conic-gradient border around the node
	 * to indicate that related-data fetches sourced from this node are
	 * currently in flight. Driven by `LoadingNodeIdsAtom` upstream and
	 * cleared automatically when the fetch resolves or the user
	 * deselects the focused node.
	 */
	isLoadingData?: boolean;
};

export type ObjectNodeType = Node<ObjectNodeData, 'objectNode'>;

const MONO_FONT = token('font.family.code');

const HANDLE_STYLE: React.CSSProperties = {
	width: 1,
	height: 1,
	opacity: 0,
	border: 'none',
	background: 'transparent',
};

// ── Fixed-card constants ────────────────────────────────────────────────
const MAX_NODE_WIDTH = 280;
const MIN_NODE_WIDTH = 120;
const MAX_TITLE_LINES = 3;
const TITLE_LINE_HEIGHT = 18;
const CARD_PADDING_X = 10;
const CARD_PADDING_Y = 8;
const ICON_WIDTH = 20;
const ICON_GAP = 6;
const BORDER = 1;
// Width consumed by chrome around the title text (icon + gap + horizontal padding).
const CARD_CHROME_X = ICON_WIDTH + ICON_GAP + CARD_PADDING_X * 2 + BORDER * 2;
const NODE_HOVER_LABEL_Z_INDEX = 100;

// Outer corner radius of the card. Used for both the visible card
// background and the radius passed to `LoadingPerimeterRing` so the
// rotating ring follows the same curvature as the host's outline.
const CARD_BORDER_RADIUS = 10;

export const ObjectNode: React.MemoExoticComponent<
	({ data, selected, id }: NodeProps<ObjectNodeType>) => JSX.Element
> = memo(({ data, selected, id }: NodeProps<ObjectNodeType>): JSX.Element => {
	const nodeData = data;
	const graphSelection = useGraphSelection();
	const { getZoom } = useReactFlow();
	const internalNode = useInternalNode(id);

	const displayName = nodeData.displayName || nodeData.id;
	const logoUrl = nodeData.productLogoUrl;

	const opacity = computeNodeOpacity(id, graphSelection);

	const typeLabel =
		nodeData.typenameDisplay ?? nodeData.typename.replace(/([a-z])([A-Z])/g, '$1 $2');

	// ── Static width measurement (once at mount) ────────────────────
	// Render an off-screen copy of the title with the same wrap behaviour
	// as the real card. To find the **longest rendered line width** (not
	// the constrained box width), use the Range API to read each client
	// rect of the wrapped text node — `inline-block + max-width` gives
	// the box width, which is always at the max once any line wraps.
	const measureRef = useRef<HTMLDivElement | null>(null);
	const [cardWidth, setCardWidth] = useState<number>(MAX_NODE_WIDTH);
	useLayoutEffect(() => {
		const el = measureRef.current;
		if (!el || !el.firstChild) {
			return;
		}
		const measuredBoxWidth = el.getBoundingClientRect().width;
		const layoutBoxWidth = el.offsetWidth;
		const currentZoom = getZoom();
		// Range rects are viewport pixels, so normalize them back to
		// ReactFlow layout units before clamping the node width.
		// Prefer ReactFlow's zoom; deriving scale from offsetWidth loses
		// sub-pixel precision because offsetWidth is integer-rounded.
		const fallbackScale =
			layoutBoxWidth > 0 && measuredBoxWidth > 0 ? measuredBoxWidth / layoutBoxWidth : 1;
		const measurementScale =
			Number.isFinite(currentZoom) && currentZoom > 0 ? currentZoom : fallbackScale;
		const safeMeasurementScale =
			Number.isFinite(measurementScale) && measurementScale > 0 ? measurementScale : 1;
		let longestLineWidth = 0;
		try {
			const range = getDocument()?.createRange();
			if (range) {
				range.selectNodeContents(el);
				const rects = range.getClientRects();
				for (let i = 0; i < rects.length; i++) {
					const unscaledWidth = rects[i].width / safeMeasurementScale;
					if (unscaledWidth > longestLineWidth) {
						longestLineWidth = unscaledWidth;
					}
				}
				range.detach?.();
			}
		} catch {
			longestLineWidth = el.offsetWidth;
		}
		if (longestLineWidth <= 0) {
			longestLineWidth = el.offsetWidth;
		}
		const computed = Math.ceil(longestLineWidth + CARD_CHROME_X);
		setCardWidth(Math.max(MIN_NODE_WIDTH, Math.min(computed, MAX_NODE_WIDTH)));
	}, [getZoom]); // mount-only in practice; displayName is stable per node id

	// ── Hover state (local — no card resize, just chip + icon swap) ─
	const [isHovered, setIsHovered] = useState(false);
	const showTypeLabel = isNodeTypeLabelVisible(id, graphSelection, isHovered, selected);

	// ── Mount-time fade-in ──────────────────────────────────────────
	// Captured once at mount so the effect doesn't re-run when the
	// upstream TTL clears the appearing flag and `isAppearing` flips
	// to undefined. `hasMounted` flips to true on the next frame,
	// driving the `opacity: 0 → 1` CSS transition.
	const appearOnMountRef = useRef<boolean>(nodeData.isAppearing === true);
	const [hasMounted, setHasMounted] = useState(!appearOnMountRef.current);
	useLayoutEffect(() => {
		if (!appearOnMountRef.current) {
			return;
		}
		const raf = requestAnimationFrame(() => setHasMounted(true));
		return () => cancelAnimationFrame(raf);
	}, []);
	const appearOpacity = hasMounted ? 1 : 0;

	// ── Loading-state rotating perimeter border ──────────────────────
	// `ExpandNodeAtom` adds this node's id to `ExpandingNodeIdsAtom`
	// while its related-data fetch is in flight; the shared
	// `<LoadingPerimeterRing>` component subscribes to that atom and
	// renders the rotating ring. We mirror the loading bool here only
	// so we can swap the static card border to transparent — otherwise
	// a faint hairline of the static border would peek through under
	// the gradient ring.
	const isLoadingData = useIsNodeLoadingData(id) || nodeData.isLoadingData === true;

	// ── Icons ───────────────────────────────────────────────────────
	const SecondaryIconComponent = useProductIcon(nodeData.productIconImportPath);

	const renderLogo = (size: number) =>
		logoUrl ? (
			<img
				src={logoUrl}
				alt=""
				style={{
					width: size,
					height: size,
					borderRadius: size <= 12 ? 2 : 3,
					flexShrink: 0,
				}}
			/>
		) : nodeData.productIcon ? (
			<span
				style={{
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
					width: size,
					height: size,
					flexShrink: 0,
					overflow: 'hidden',
					transform: 'scale(0.8)',
					transformOrigin: 'center',
				}}
			>
				{nodeData.productIcon}
			</span>
		) : (
			<span
				style={{
					display: 'grid',
					placeItems: 'center',
					width: size,
					height: size,
					borderRadius: 3,
					fontSize: 9,
					fontWeight: 600,
					backgroundColor: '#52525b',
					color: '#f5f5f5',
					flexShrink: 0,
				}}
			>
				{(nodeData.typename || '?').charAt(0)}
			</span>
		);

	// Leading icon stays as the product logo at all times.
	const leadingIconElement = renderLogo(ICON_WIDTH);

	// Small secondary icon for the hover chip — same dimensions as the
	// 12px logo we previously rendered there. Currently lazy-loaded via
	// `useProductIcon`; falls back to nothing if no resource-type icon
	// is registered for this node.
	const chipSecondaryIcon = SecondaryIconComponent ? (
		<span
			style={{
				width: 12,
				height: 12,
				flexShrink: 0,
				display: 'inline-flex',
				alignItems: 'center',
				justifyContent: 'center',
			}}
		>
			<SecondaryIconComponent label="" color="currentColor" LEGACY_size="small" />
		</span>
	) : (
		renderLogo(12)
	);
	const hoverLabelX = internalNode?.internals.positionAbsolute.x ?? 0;
	const hoverLabelY = internalNode?.internals.positionAbsolute.y ?? 0;
	const hoverLabel = (
		<div
			style={{
				display: 'inline-flex',
				alignItems: 'center',
				gap: 4,
				padding: '3px 8px',
				borderRadius: 6,
				backgroundColor: '#101214',
				border: '1px solid #3D3F43',
				fontFamily: MONO_FONT,
				fontSize: 10,
				lineHeight: '14px',
				color: 'rgba(221,222,225,0.7)',
				whiteSpace: 'nowrap',
				pointerEvents: 'none',
				opacity: showTypeLabel ? 1 : 0,
				transform: `translateY(${showTypeLabel ? 0 : 2}px)`,
				transition: 'opacity 0.12s ease-out, transform 0.12s ease-out',
			}}
		>
			{chipSecondaryIcon}
			<span>{typeLabel}</span>
		</div>
	);

	return (
		<div
			style={{
				opacity: opacity * appearOpacity,
				transition: 'opacity 0.3s ease-out',
				position: 'relative',
			}}
			onMouseEnter={() => setIsHovered(true)}
			onMouseLeave={() => setIsHovered(false)}
			onFocus={() => setIsHovered(true)}
			onBlur={() => setIsHovered(false)}
		>
			<Handle type="target" position={Position.Left} style={{ ...HANDLE_STYLE, left: 0 }} />

			{/* Hover chip (logo + type label) — appears above the card and raised edge overlays */}
			<EdgeLabelRenderer>
				<div
					style={{
						position: 'absolute',
						transform: `translate(${hoverLabelX}px, ${hoverLabelY - 4}px) translateY(-100%)`,
						zIndex: NODE_HOVER_LABEL_Z_INDEX,
					}}
				>
					{hoverLabel}
				</div>
			</EdgeLabelRenderer>

			{/* Off-screen measurement element — used once at mount.
				    Shrink-wraps to the longest rendered line within the same
				    constraints the visible title applies. */}
			<div
				ref={measureRef}
				aria-hidden="true"
				style={{
					position: 'absolute',
					top: -9999,
					left: -9999,
					visibility: 'hidden',
					display: 'inline-block',
					maxWidth: MAX_NODE_WIDTH - CARD_CHROME_X,
					fontFamily: MONO_FONT,
					fontSize: 14,
					lineHeight: `${TITLE_LINE_HEIGHT}px`,
					wordBreak: 'break-word',
				}}
			>
				{displayName}
			</div>

			{/* Rotating perimeter ring shown while related-data fetches are
				    in flight. The ring renders iff this node id is in
				    `ExpandingNodeIdsAtom`; sizing follows ReactFlow's
				    measured node dimensions so the outline matches the
				    actual rendered card on any aspect ratio. */}
			<LoadingPerimeterRing nodeId={id} borderRadius={CARD_BORDER_RADIUS} />

			<div
				style={{
					backgroundColor: '#101214',
					// Hide the static border underneath the rotating gradient
					// ring so only the gradient is visible while loading.
					border: isLoadingData
						? '1px solid transparent'
						: `1px solid ${selected || isHovered ? '#FFFFFF' : '#3D3F43'}`,
					borderRadius: CARD_BORDER_RADIUS,
					padding: `${CARD_PADDING_Y}px ${CARD_PADDING_X}px`,
					cursor: 'pointer',
					width: cardWidth,
					maxWidth: MAX_NODE_WIDTH,
					overflow: 'hidden',
					fontFamily: MONO_FONT,
					fontSize: 14,
					lineHeight: `${TITLE_LINE_HEIGHT}px`,
					color: '#dddee1',
					boxShadow:
						selected || isHovered
							? '0 0 0 1px rgba(245,247,251,0.45), 0 10px 24px rgba(0,0,0,0.28)'
							: undefined,
					transition: 'border-color 0.1s ease-out, box-shadow 0.1s ease-out',
					// Sit above the rotating ring so card content stays readable.
					position: 'relative',
					zIndex: 1,
				}}
			>
				<div
					style={{
						display: 'flex',
						alignItems: 'flex-start',
						gap: ICON_GAP,
					}}
				>
					<span
						style={{
							display: 'inline-flex',
							alignItems: 'center',
							height: TITLE_LINE_HEIGHT,
							flexShrink: 0,
						}}
					>
						{leadingIconElement}
					</span>
					<span
						style={{
							display: '-webkit-box',
							WebkitLineClamp: MAX_TITLE_LINES,
							WebkitBoxOrient: 'vertical' as any,
							overflow: 'hidden',
							wordBreak: 'break-word',
							lineHeight: `${TITLE_LINE_HEIGHT}px`,
							flex: 1,
							minWidth: 0,
						}}
					>
						{displayName}
					</span>
				</div>
			</div>
			<Handle type="source" position={Position.Right} style={{ ...HANDLE_STYLE, right: 0 }} />
		</div>
	);
});

ObjectNode.displayName = 'ObjectNode';
